import type { ISportAngle } from "~/videos/types/event-angles";
import { isClipAnglesNotContinuousPlaylistError } from "~/videos/helpers/errors";

export type TClipAnglesStatus = "loading" | "success" | "empty" | "incompatible-playlist" | "error";

interface IUseClipAnglesAvailabilityOptions {
  availableClipAngles?: Ref<ISportAngle[] | undefined> | ComputedRef<ISportAngle[] | undefined>;
  clipAnglesError?: Ref<unknown> | ComputedRef<unknown>;
  clipAnglesLoading?: Ref<boolean | undefined> | ComputedRef<boolean | undefined>;
}

/**
 * Shared clip-angles availability/status logic for the "Download clipped video" and
 * "Downloadable files" (share) menu items, which both need to know whether clipped video is
 * available for a given event/video and, if not, why (loading / empty / incompatible
 * playlist / fetch error), to drive their disabled + tooltip states.
 *
 * The live clip-angles fetch state (loading/error/result) is always authoritative here: it
 * reflects what was actually fetchable, as opposed to a raw "does the event already have a
 * clipped-angles entry" existence check, which can be true even when the live fetch fails
 * (e.g. a 500 from an incompatible VidSwap playlist class).
 */
export function useClipAnglesAvailability(options: IUseClipAnglesAvailabilityOptions) {
  const { t } = useI18n();
  const { availableClipAngles, clipAnglesError, clipAnglesLoading } = options;

  const clipAnglesStatus = computed<TClipAnglesStatus>(() => {
    if (unref(clipAnglesLoading)) return "loading";
    const error = unref(clipAnglesError);
    if (error) return isClipAnglesNotContinuousPlaylistError(error) ? "incompatible-playlist" : "error";
    return unref(availableClipAngles)?.length ? "success" : "empty";
  });

  const isClipAnglesUnavailable = computed(() => {
    const status = clipAnglesStatus.value;
    return status === "empty" || status === "incompatible-playlist" || status === "error";
  });

  const clipAnglesUnavailableTooltip = computed(() => {
    switch (clipAnglesStatus.value) {
      case "loading":
        return t("labels.loading");
      case "empty":
        return t("errors.no_clip_angles_available");
      case "incompatible-playlist":
        return t("errors.clip_angles_not_continuous_playlist");
      case "error":
        return t("errors.clip_angles_fetch_failed");
      default:
        return undefined;
    }
  });

  return { clipAnglesStatus, isClipAnglesUnavailable, clipAnglesUnavailableTooltip };
}
